import ReduxStore from 'src/types/store/reduxStore';
import { ComparisonPeriod, useAppSelector, WeekRange } from 'src/utils/Hooks';
import {
  getNextYearWeekId,
  getPreviousWeekId,
  getPreviousYearWeekId,
  timestampToWeekId,
  weekIdToTimestamp
} from 'src/utils/dateUtils';
import { store } from 'src/main';
import convertLineChartSeriesToDelimitedData, {
  Series
} from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import saveAs from 'file-saver';
import _isNil from 'lodash/isNil';
import { safeDivide } from 'src/utils/app';
import { StackedChartDataSet } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewAnalysis/WeeklyReviewVolumeByStarRatingChart';

interface CreateNonComparisonLegendArgs {
  /**
   * mainTimePeriod from Redux. Only the displayName property is needed.
   */
  chartDisplayTimePeriod: Pick<ReduxStore['mainTimePeriod'], 'displayName'>;
  /**
   * Main label for the legend div.
   */
  mainLegendMetricValue: { value: string | number; prefix?: string; suffix?: string };
  /**
   * If false, will not display `metricsDataByTimeSeries`
   */
  isSameMetricComparison: boolean;
  /**
   * Additional information that will be appended after the time period.
   * Will not be used and can be null if `isSameMetricComparison` is false.
   */
  metricsDataByTimeSeries: {
    displayName: string;
  } | null;
  /**
   * An array of legend divs that will be rendered in the chart
   * legend. The new legend div will be appended to the end of this array.
   */
  legendDivs: string[];
}

/**
 * Creates a legend label for a chart with the main time
 * period displayed as a subtitle.
 */
export const createNonComparisonLegendDivs = ({
  chartDisplayTimePeriod,
  mainLegendMetricValue,
  isSameMetricComparison,
  metricsDataByTimeSeries,
  legendDivs
}: CreateNonComparisonLegendArgs) => {
  let mainLegendDiv = `<div>${mainLegendMetricValue.prefix || ''}${mainLegendMetricValue.value}${
    mainLegendMetricValue.suffix || ''
  }</div>
  <div class="legend__primary-date" style="margin-top:5px;">${chartDisplayTimePeriod.displayName}</div>`;
  if (!isSameMetricComparison) {
    mainLegendDiv += `<br/><div class="legend__primary-date" style="margin-top:5px;">${metricsDataByTimeSeries.displayName}</div>`;
  }
  legendDivs.push(mainLegendDiv);
};

/**
 * Given a primary and comparison data set, return data sets that can be
 * plotted on the spline chart.
 *
 * For example, when comparing against a previous year we will add a year
 * to the comparison data set so that the charts overlap and can be compared.
 * Also, when comparing agains the prior period we will connect the data set
 * to form a single continuous line.
 */
export const getAdjustedDataSetsForSpline = (
  primaryData: number[][],
  secondaryData: number[][]
): {
  primaryData: number[][];
  secondaryData: number[][];
} => {
  const queryParams = new URLSearchParams(window.location.search);

  // Comparing to prior year, add a year to the comparison set
  if (queryParams.get('pid') === 'prior-year') {
    return {
      primaryData,
      secondaryData: secondaryData.map(([timestamp, value]) => [
        weekIdToTimestamp(getNextYearWeekId(timestampToWeekId(timestamp))),
        value
      ])
    };
  }

  // Comparing to prior period, connect the data sets
  const lastSecondary = secondaryData?.[secondaryData.length - 1];
  return {
    primaryData: [...(!_isNil(lastSecondary) ? [lastSecondary] : []), ...primaryData],
    secondaryData
  };
};

/**
 * Returns the primary date range to use for a spline chart.
 * If the main time period is 1w and the prior year comparison is selected,
 * the full YTD range will be used.
 */
export const useSplineMainDateRange = () => {
  const {
    startWeek,
    endWeek,
    id: periodId,
    availableMainTimePeriods
  } = useAppSelector((state) => state.mainTimePeriod);
  const queryParams = new URLSearchParams(window.location.search);

  if (periodId === '1w') {
    const ytdPeriod = availableMainTimePeriods.find((period) => period.id === 'ytd');

    if (!ytdPeriod) {
      throw new Error('YTD period not found while evaluating date range for 1w');
    }

    // Use full YTD range
    if (queryParams.get('pid') === 'prior-year') {
      return {
        startWeek: ytdPeriod.startWeek,
        endWeek: ytdPeriod.endWeek
      };
    }
  }

  return {
    startWeek,
    endWeek
  };
};

/**
 * Returns the comparison date range to use for a spline chart.
 * If the main time period is 1w and the prior year comparison is selected,
 * the full YTD range will be used.
 */
export const useSplineComparisonDateRange = (): {
  comparisonStartWeek: number;
  comparisonEndWeek: number;
} => {
  const { startWeek, id: periodId, availableMainTimePeriods } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const queryParams = new URLSearchParams(window.location.search);

  // For 1 week, we show YTD data with a dot on the last week,
  // so use the time range for the last year
  if (periodId === '1w') {
    const ytdPeriod = availableMainTimePeriods.find((period) => period.id === 'ytd');

    if (!ytdPeriod) {
      throw new Error('YTD period not found while evaluating comparison for 1w');
    }

    // Get full year comparison
    if (queryParams.get('pid') === 'prior-year') {
      return {
        comparisonStartWeek: getPreviousYearWeekId(ytdPeriod.startWeek),
        comparisonEndWeek: getPreviousWeekId(ytdPeriod.startWeek)
      };
    }
  }

  if (periodId === 'ytd') {
    return {
      comparisonStartWeek,
      comparisonEndWeek: getPreviousWeekId(startWeek)
    };
  }

  return {
    comparisonStartWeek,
    comparisonEndWeek
  };
};

/**
 * Computes the totals to be displayed in a spline chart legend.
 * Do not pass in the adjusted data sets from getAdjustedDataSetsForSpline
 * because those can have extra values since they are for display in the spline chart.
 */
export const getSplineTotals = <T>(
  primaryData: number[][],
  secondaryData: number[][],
  options:
    | { func: 'avg' }
    | { func: 'sum' }
    | { func: 'last' }
    | {
        func: 'custom';
        /**
         * When on YTD view, the secondary data needs to be filtered to only
         * include values up to where the primary data set ends. This function
         * defines how to filter the secondary data up to the last week ID
         * where the primary data ends.
         *
         * weekId will be null if not on the YTD view, in that case the function
         * should just return the secondary data as is.
         */
        filterSecondaryData: (weekId: number | null) => T;
        /**
         * Custom function to compute the total. This function will be called
         * with the filtered secondary data.
         */
        customFunc: ({ filteredSecondaryData }: { filteredSecondaryData: T }) => {
          primaryTotal: number;
          secondaryTotal: number;
        };
      }
): { primaryTotal: number; secondaryTotal: number } => {
  const queryParams = new URLSearchParams(window.location.search);

  if (options.func === 'last') {
    const secondaryWeekIdToValueMap = secondaryData.reduce((acc, [timestamp, value]) => {
      acc.set(timestampToWeekId(timestamp), value);
      return acc;
    }, new Map<number, number>());

    const lastValue = primaryData?.[primaryData.length - 1];

    const secondaryTotal =
      queryParams.get('wr') !== '1w' && queryParams.get('pid') === 'prior-period'
        ? secondaryData?.[secondaryData.length - 1]?.[1]
        : null;

    return {
      primaryTotal: lastValue ? lastValue[1] : 0,
      secondaryTotal:
        secondaryTotal !== null
          ? secondaryTotal
          : queryParams.get('pid') === 'prior-year' // Display the value from one year ago for prior year, or the previous week for prior period
          ? secondaryWeekIdToValueMap.get(getPreviousYearWeekId(timestampToWeekId(lastValue?.[0]))) ?? 0
          : secondaryWeekIdToValueMap.get(getPreviousWeekId(timestampToWeekId(lastValue?.[0]))) ?? 0
    };
  }

  if (queryParams.get('wr') === '1w') {
    return getSplineTotals(primaryData, secondaryData, { func: 'last' });
  }

  if (options.func === 'custom') {
    let lastWeekId = null;
    if (queryParams.get('wr') === 'ytd' && queryParams.get('pid') === 'prior-year') {
      lastWeekId = timestampToWeekId(primaryData?.[primaryData.length - 1]?.[0]);
    }

    const filteredSecondaryData = options.filterSecondaryData(lastWeekId);
    return options.customFunc({ filteredSecondaryData });
  }

  if (options.func === 'sum') {
    const lastTimestamp = primaryData?.[primaryData.length - 1]?.[0];

    const secondaryTotal = secondaryData
      .filter(([timestamp]) =>
        // Only sum up the secondary data set up to the date where the primary data ends
        // when comparing YTD with prior year
        queryParams.get('wr') === 'ytd' && queryParams.get('pid') === 'prior-year'
          ? weekIdToTimestamp(getNextYearWeekId(timestampToWeekId(timestamp))) <= lastTimestamp
          : true
      )
      .reduce((acc, [, value]) => acc + value, 0);

    return {
      primaryTotal: primaryData.reduce((acc, [_, value]) => acc + value, 0),
      secondaryTotal
    };
  }

  if (options.func === 'avg') {
    const { primaryTotal: primarySum, secondaryTotal: secondarySum } = getSplineTotals(primaryData, secondaryData, {
      func: 'sum'
    });

    return {
      primaryTotal: safeDivide(primarySum, primaryData.length),
      secondaryTotal: safeDivide(secondarySum, primaryData.length) // Divide by primary length because on YTD we only count up to where primary ends
    };
  }

  return {
    primaryTotal: 0,
    secondaryTotal: 0
  };
};

/**
 * Save spline chart data as a CSV
 */
export const saveSplineAsDelimitedData = (primaryData: number[][], secondaryData: number[][], title: string) => {
  try {
    const { comparisonTimePeriod, mainTimePeriod } = store.getState();

    const data = convertLineChartSeriesToDelimitedData(
      [
        {
          type: 'spline',
          name: 'Primary',
          data: primaryData.map((row) => ({
            x: row[0],
            y: row[1]
          }))
        } as unknown as Series,
        {
          type: 'spline',
          name: 'Secondary',
          data: secondaryData.map((row) => ({
            x: row[0],
            y: row[1]
          }))
        } as unknown as Series
      ],
      comparisonTimePeriod.id === 'prior-period',
      comparisonTimePeriod,
      mainTimePeriod
    );

    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${title}.csv`);
  } catch (e) {
    console.error('Error saving spline chart as CSV: ', e);
  }
};

/**
 * Save bar chart data as CSV
 */
export const saveBarChartAsDelimitedData = (
  primaryData: [string, number][],
  secondaryData: [string, number][],
  options: {
    /**
     * Title of the CSV file to be saved
     */
    fileTitle?: string;
    /**
     * Label for the metric column in the CSV file
     */
    metricLabel?: string;
  } = {}
) => {
  const { fileTitle = 'csv_data', metricLabel = '' } = options;
  try {
    const { comparisonTimePeriod, mainTimePeriod } = store.getState();

    const primaryDataMap = primaryData.reduce((acc, [name, value]) => {
      acc.set(name, value);
      return acc;
    }, new Map<string, number>());

    const secondaryDataMap = secondaryData.reduce((acc, [name, value]) => {
      acc.set(name, value);
      return acc;
    }, new Map<string, number>());

    const uniqueNames = new Set([...primaryData.map(([name]) => name), ...secondaryData.map(([name]) => name)]);
    const headers = [
      'Entity',
      metricLabel ? `"${metricLabel}: ${comparisonTimePeriod.displayName}"` : `"${comparisonTimePeriod.displayName}"`,
      metricLabel ? `"${metricLabel}: ${mainTimePeriod.displayName}"` : `"${mainTimePeriod.displayName}"`
    ];

    const dataRows = Array.from(uniqueNames).map((name) => {
      return [`"${name}"`, `"${secondaryDataMap.get(name) ?? 0}"`, `"${primaryDataMap.get(name) ?? 0}"`];
    });

    const data = [headers, ...dataRows].map((row) => row.join(',')).join('\n');

    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${fileTitle}.csv`);
  } catch (e) {
    console.error('Error saving bar chart as CSV: ', e);
  }
};

/**
 * Save bar chart that does not take secondaryData
 */
export const saveSingleBarChartAsDelimitedData = (
  primaryData: [string, number][],
  options: {
    /**
     * Title of the CSV file to be saved
     */
    fileTitle?: string;
    /**
     * Label for the metric column in the CSV file
     */
    metricLabel?: string;
  } = {}
) => {
  const { fileTitle = 'csv_data', metricLabel = '' } = options;

  try {
    const { mainTimePeriod } = store.getState();
    const headers = [
      'Entity',
      metricLabel ? `"${metricLabel}: ${mainTimePeriod.displayName}"` : `"${mainTimePeriod.displayName}"`
    ];

    const dataRows = primaryData.map(([name, value]) => {
      return [`"${name}"`, `"${value}"`];
    });

    const data = [headers, ...dataRows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${fileTitle}.csv`);
  } catch (e) {
    console.error('Error Saving single bar chart as CSV: ', e);
  }
};

/**
 * Save bar chart that does not take secondaryData
 */
export const saveWeeklyStackedBarChartAsDelimitedData = ({
  chartData,
  categoryData,
  options = {}
}: {
  chartData: StackedChartDataSet[];
  categoryData?: string[];
  options?: any;
}) => {
  const { fileTitle = 'csv_data' } = options;

  try {
    // Define headers
    const headers = ['Week ID', 'Positive', 'Neutral', 'Negative'];

    // Initialize an array to store the data rows
    const dataRows = [];

    // Loop through the category data to create rows
    categoryData.forEach((week, index) => {
      const positive = chartData.find((item) => item.name === 'Positive')?.data?.[index]?.y ?? 0;
      const neutral = chartData.find((item) => item.name === 'Neutral')?.data?.[index]?.y ?? 0;
      const negative = chartData.find((item) => item.name === 'Negative')?.data?.[index]?.y ?? 0;

      dataRows.push([week, positive, neutral, negative]);
    });

    // Construct CSV data as a string
    const data = [headers, ...dataRows].map((row) => row.join(',')).join('\n');

    // Create a Blob from the data and use FileSaver to save it as a CSV file
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${fileTitle}.csv`);
  } catch (e) {
    console.error('Error saving stacked bar chart as CSV: ', e);
  }
};

export const saveStandardStackedBarChartAsDelimitedData = ({
  chartSeries,
  options = { headers: [] }
}: {
  chartSeries: StackedChartDataSet[];
  options: { headers: string[]; fileTitle?: string };
}) => {
  const { fileTitle = 'csv_data', headers = [] } = options;
  try {
    const positiveData = chartSeries.find((series) => series.name === 'Positive')?.data;
    const neutralData = chartSeries.find((series) => series.name === 'Neutral')?.data;
    const negativeData = chartSeries.find((series) => series.name === 'Negative')?.data;

    const csvData = positiveData.map((positiveDatum, index) => {
      const topic = positiveDatum?.name || '';
      const positiveValue = positiveDatum.y;
      const neutralValue = neutralData[index]?.y ?? 0;
      const negativeValue = negativeData[index]?.y ?? 0;

      return [topic, positiveValue, neutralValue, negativeValue];
    });
    const csvDataString = [headers, ...csvData].map((row) => row.join(',')).join('\n');

    const blob = new Blob([csvDataString], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${fileTitle}.csv`);
  } catch (error) {
    console.error('Failed to save chart as delimited data ', error);
  }
};

// calculates derived metric - this takes weekId, and values for any division of data (primarily using in conversion rate )
export const calculateDerivedMetricByWeekId = ({
  dataSet1,
  dataSet2,
  returnTimeStamp = false
}: {
  dataSet1: [number, number][];
  dataSet2: [number, number][];
  returnTimeStamp?: boolean;
}) => {
  if (!dataSet1 || !dataSet2 || dataSet1.length === 0 || dataSet2.length === 0) {
    return [];
  }

  const dataSet1Map = new Map<number, number>();
  const dataSet2Map = new Map<number, number>();

  dataSet1.forEach(([weekId, value]) => {
    dataSet1Map.set(+weekId, value);
  });

  dataSet2.forEach(([weekId, value]) => {
    dataSet2Map.set(+weekId, value);
  });

  const allWeekIds = new Set([...dataSet1Map.keys(), ...dataSet2Map.keys()]);

  const derivedMetricData = Array.from(allWeekIds)
    .map((weekId) => {
      const value1 = dataSet1Map.get(weekId) ?? 0;
      const value2 = dataSet2Map.get(weekId) ?? 0;

      const derivedMetric = safeDivide(value1, value2);

      return [returnTimeStamp ? weekIdToTimestamp(weekId) : weekId, derivedMetric] as [number, number];
    })
    .filter((item): item is [number, number] => item !== null)
    .sort(([a], [b]) => a - b);

  return derivedMetricData;
};

/**
 * @deprecated Use getPercentageAxisConfig instead - will keep this in case we want this in few days.
 * Calculates a "nice" step value for chart axes.
 * Rounds raw step values to aesthetically pleasing intervals: 1, 1.5, 2, 2.5, 3, 5, 10 (×10^n)
 *
 * @param rawStep - The raw step value to round
 * @returns A "nice" step value for chart axis display
 */
export const getRoundStep = (rawStep: number): number => {
  if (rawStep <= 0) return 1;

  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;

  if (normalized <= 1) return magnitude;
  if (normalized <= 1.5) return 1.5 * magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 2.5) return 2.5 * magnitude;
  if (normalized <= 3) return 3 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
};

/**
 * Calculates the optimal yMax and step for percentage chart axes.
 * Prioritizes tight fit to data with yMax rounded up to appropriate unit.
 * - For max < 2%: rounds to nearest 0.5%
 * - For max 2-5%: rounds to nearest 1%
 * - For max >= 5%: rounds to nearest 5%
 *
 * @param maxValue - The maximum value in the data (as decimal, e.g., 0.42 for 42%)
 * @param tickCount - Number of ticks (default 4)
 * @returns { yMax, step } both as decimals
 */
export const getPercentageAxisConfig = (maxValue: number, tickCount: number = 4): { yMax: number; step: number } => {
  if (maxValue <= 0) return { yMax: 0.05, step: 0.05 / (tickCount - 1) };

  const maxPercent = maxValue * 100;
  const intervals = tickCount - 1; // 4 ticks = 3 intervals

  // Add small padding (~5%) and round up to appropriate unit based on magnitude
  const paddedMax = maxPercent * 1.05;

  let roundUnit: number;
  if (maxPercent < 2) {
    roundUnit = 0.5; // Round to nearest 0.5% for very small values
  } else if (maxPercent < 5) {
    roundUnit = 1; // Round to nearest 1% for small values
  } else {
    roundUnit = 5; // Round to nearest 5% for larger values
  }

  let yMaxPercent = Math.ceil(paddedMax / roundUnit) * roundUnit;

  // Ensure yMax is at least slightly above the actual max
  if (yMaxPercent <= maxPercent) {
    yMaxPercent += roundUnit;
  }

  // Cap at 100%
  yMaxPercent = Math.min(yMaxPercent, 100);

  const stepPercent = yMaxPercent / intervals;

  return {
    yMax: yMaxPercent / 100,
    step: stepPercent / 100
  };
};
